import { useCallback, useEffect, useRef, useState } from 'react';
import { configSettings } from '@/lib/config';

interface RecaptchaWindow extends Window {
    grecaptcha?: {
        enterprise: {
            ready: (callback: () => void) => void;
            execute: (siteKey: string, options: { action: string }) => Promise<string>;
        };
    };
}

declare const window: RecaptchaWindow;

/**
 * Custom hook for Google reCAPTCHA Enterprise
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { getToken, isLoading, isReady, error } = useRecaptcha();
 *
 *   const handleSubmit = async () => {
 *     if (!isReady) return;
 *
 *     try {
 *       const token = await getToken('CreateSetupIntent');
 *       // Pass token with API call
 *       await api.post('/endpoint', { token, ...otherData });
 *     } catch (err) {
 *       console.error('Recaptcha error:', err);
 *     }
 *   };
 *
 *   return <button onClick={handleSubmit} disabled={!isReady}>Submit</button>;
 * }
 * ```
 *
 * @returns Object containing getToken function, loading state, and error state
 */
export function useRecaptcha() {
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const scriptLoadedRef = useRef(false);
    const readyRef = useRef(false);

    useEffect(() => {
        const loadRecaptchaScript = async () => {
            // Check if script is already loaded
            if (scriptLoadedRef.current || window.grecaptcha) {
                setIsLoading(false);
                if (window.grecaptcha) {
                    window.grecaptcha.enterprise.ready(() => {
                        readyRef.current = true;
                        setIsLoading(false);
                    });
                }
                return;
            }

            try {
                const script = document.createElement('script');
                script.src = `https://www.google.com/recaptcha/enterprise.js?render=${configSettings.captchaSiteKey}`;
                script.async = true;

                script.onload = () => {
                    scriptLoadedRef.current = true;

                    if (window.grecaptcha) {
                        window.grecaptcha.enterprise.ready(() => {
                            readyRef.current = true;
                            setIsLoading(false);
                        });
                    }
                };

                script.onerror = () => {
                    setError('Failed to load reCAPTCHA script');
                    setIsLoading(false);
                };

                document.head.appendChild(script);
            } catch (err) {
                setError(`Error loading reCAPTCHA ${err}`);
                setIsLoading(false);
            }
        };

        loadRecaptchaScript();
    }, []);

    /**
     * Get a reCAPTCHA token for a specific action
     * @param action - The action name for this token (e.g., 'CreateSetupIntent', 'login', 'submit_form')
     * @returns Promise that resolves to the reCAPTCHA token
     */
    const getToken = useCallback(
        async (action: string): Promise<string> => {
            if (!configSettings.captchaSiteKey) {
                throw new Error('reCAPTCHA site key is not configured');
            }

            if (error) {
                throw new Error(error);
            }

            if (!window.grecaptcha) {
                throw new Error('reCAPTCHA is not loaded');
            }

            try {
                const token = await window.grecaptcha.enterprise.execute(configSettings.captchaSiteKey, { action });
                return token;
            } catch (err) {
                const errorMessage = err instanceof Error ? err.message : 'Failed to get reCAPTCHA token';
                throw new Error(errorMessage);
            }
        },
        [error]
    );

    return {
        getToken,
        isLoading,
        error,
        isReady: !isLoading && !error && readyRef.current,
    };
}
